import { useNavigate } from "react-router-dom";

import { PermissionsEnum } from "@/auth/interfaces";
import {
  EstadosPreagendamientoEnumChoice,
  ROUTER_PATHS,
} from "@/shared";
import { GeneralTable, TableTabsScene } from "@/shared/components/ui";
import { TABLE_CONSTANTS } from "@/shared/constants/ui";
import {
  useCheckPermissions,
  useTableServerSideFiltering,
} from "@/shared/hooks";
import { useTableFilter } from "@/shared/hooks/useTableFilter";
import {
  AlertSeverityEnum,
} from "@/shared/interfaces";
import { hasPermission } from "@/shared/utils";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { Edit, LockOpen } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import { useTableColumnsPreagendamientoPymes } from "../../hook/useTableColumnsPreagendamientoPymes";
import { getPreagendamientoPymesById, useFetchPreagendamientosPymes, useFetchPutPreagendamientoPymes } from "@/shared/actions/erp/operaciones/preagendamiento/preagendamiento_pymes.actions";
import { IPreagendamientoPymes } from "@/shared/interfaces/erp/ventas_pymes/preagendamiento_pymes.interface";
import useEstadoInstalacionSofnet from "@/shared/hooks/useEstadoInstalacionSofnet";

export type TableAgendamientoPymesProps = {
  estado_aceptacion?: boolean;
  estado_preagendamiento?: string;
  enable_action?: boolean
  solicitar_tecnico?: boolean
};

const TablePreAgendamientoSoportePymes: React.FC<
  TableAgendamientoPymesProps
> = ({
  enable_action = true,
  estado_aceptacion = false,
  estado_preagendamiento,
  solicitar_tecnico = false
}) => {
    //PERMISSIONS
    useCheckPermissions(PermissionsEnum.api_view_preagendamientopymes);
    const isPyLAdmin = hasPermission(
      PermissionsEnum.pyl_administrator_permission
    );

    //CONST
    const returnUrlAgendamientosSPPage =
      ROUTER_PATHS.soportePymes.preagendamientoCreateNav;
    const navigate = useNavigate();

    //GLOBALS
    const setAlert = useUiStore((s) => s.setAlert);
    const { filterObject, columnFilters, setColumnFilters } =
      useTableServerSideFiltering();

    const user = useAuthStore((s) => s.user);

    ///* table
    const {
      globalFilter,
      pagination,
      searchTerm,
      onChangeFilter,
      setPagination,
    } = useTableFilter();
    const { pageIndex, pageSize } = pagination;

    ///* fetch data
    const {
      data: PreagendamientosPagingRes,
      isLoading,
      isRefetching,
    } = useFetchPreagendamientosPymes({
      enabled: true,
      params: {
        page: pageIndex + 1,
        page_size: pageSize,
        estado_aceptacion: estado_aceptacion,
        identificacion: searchTerm,
        solicitar_tecnico,
        ...filterObject,
        filterByState: false,
        estado_preagendamiento_in: estado_preagendamiento,
      },
    });

    ///* mutations --------------------
    const updatePreAgendamientoMutationOcupado =
      useFetchPutPreagendamientoPymes({
        enableRedirect: false,
        enableSuccessAlert: false,
      });


    const {
      handleValidacion
    } = useEstadoInstalacionSofnet()

    ///* handlers
    const onEdit = async (preagendamiento: IPreagendamientoPymes) => {

      const aux = await handleValidacion(preagendamiento.id_instalacion_externa);

      if (aux) return;

      const selectedPreagendamiento = await getPreagendamientoPymesById(
        preagendamiento.id_preagendamiento_pymes!
      );


      if (selectedPreagendamiento?.data.estado_preagendamiento == EstadosPreagendamientoEnumChoice.PENDIENTE_TECNICO
        && !selectedPreagendamiento.data.flota_adicional) {
        return setAlert(true, 'Solicitud esta en proceso', AlertSeverityEnum.info)
      }
      //valido que exista en preagendamientos
      if (!selectedPreagendamiento)
        return setAlert(
          true,
          "No se puede acceder a este registro. Por favor, recargue la página.",
          AlertSeverityEnum.info
        );

      //obtengo el usuario que se encuentra gestionando el preagendamiento
      const actualUser = selectedPreagendamiento?.data?.user_gestionando;

      if (actualUser != null && actualUser != user?.username)
        return setAlert(
          true,
          "No puede acceder a este preagendamiento porque ya se encuentra siendo procesado.",
          AlertSeverityEnum.info
        );

      setAlert(
        true,
        "En este momento solo usted puede modificar este registro.",
        AlertSeverityEnum.info
      );


      navigate(
        `${returnUrlAgendamientosSPPage}/${preagendamiento.id_preagendamiento_pymes}`
      );

      await updatePreAgendamientoMutationOcupado.mutateAsync({
        id: preagendamiento.id_preagendamiento_pymes!,
        data: {
          user_gestionando: user?.username,
        },
      });
    };

    const unlockPreagendamiento = async (preagendamiento: IPreagendamientoPymes) => {
      const selectedPreagendamiento = await getPreagendamientoPymesById(
        preagendamiento.id_preagendamiento_pymes!
      );
      //valido que exista en preagendamientos
      if (!selectedPreagendamiento)
        return setAlert(
          true,
          "No se puede acceder a este registro. Por favor, recargue la página.",
          AlertSeverityEnum.info
        );

      //obtengo el usuario que se encuentra gestionando el preagendamiento
      const actualUser = selectedPreagendamiento?.data?.user_gestionando;
      if (actualUser == null)
        return setAlert(
          true,
          "El registro no esta siendo gestionado. Acceda normalmente.",
          AlertSeverityEnum.info
        );

      //si esta libre setea el usuario que esta actualmente logueado
      await updatePreAgendamientoMutationOcupado.mutateAsync({
        id: preagendamiento.id_preagendamiento_pymes!,
        data: {
          user_gestionando: null,
        },
      });

      setAlert(true, "Preagendamiento liberado.", AlertSeverityEnum.info);
    };


    ///* columns
    const { preagendamientoColumns } =
      useTableColumnsPreagendamientoPymes();


    return (
      <TableTabsScene
        globalFilter={globalFilter}
        onChangeFilter={onChangeFilter}
      >
        <GeneralTable<IPreagendamientoPymes>
          columns={preagendamientoColumns}
          data={PreagendamientosPagingRes?.data?.data || []}
          isLoading={isLoading}
          isRefetching={isRefetching}
          // // filters - server side
          enableManualFiltering={true}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          // // search
          enableGlobalFilter={false}
          // // pagination
          pagination={pagination}
          onPaging={setPagination}
          rowCount={PreagendamientosPagingRes?.data?.count}
          // // actions ========
          enableColumnActions={hasPermission(
            PermissionsEnum.api_change_preagendamiento
          )}
          actionsColumnSize={TABLE_CONSTANTS.ACTIONCOLUMN_WIDTH}
          canEdit={false}
          canDelete={false}
          enableEditing={enable_action}
          showCustomActionBtnSpace={enable_action}
          customActionBtnSpace={(value) => {
            return (
              <Grid container spacing={2}>
                <Grid item xs={12}>

                  <IconButton onClick={() => onEdit(value)}>
                    <Edit />
                  </IconButton>
                  {isPyLAdmin ? (
                    <IconButton onClick={() => unlockPreagendamiento(value)}>
                      <LockOpen />
                    </IconButton>
                  ) : null}
                </Grid>
              </Grid>
            );
          }}
        />
      </TableTabsScene>
    );
  };

export default TablePreAgendamientoSoportePymes;
